import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Folder,
  FolderPlus,
  KeyRound,
  Lock,
  Mail,
  ScrollText,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users,
} from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../auth';
import { AvatarCropper, type CropRect } from '../components/AvatarCropper';

type AccessRole = 'admin' | 'member' | 'viewer';

interface AccessFlags {
  can_manage_files: boolean;
  can_delete_files: boolean;
  can_manage_privacy: boolean;
  can_create_folders: boolean;
  can_delete_folders: boolean;
  can_manage_logs: boolean;
  can_manage_credentials: boolean;
  can_manage_members: boolean;
}

interface Member extends AccessFlags {
  member_user_id: string;
  email?: string;
  display_name?: string;
  role: 'owner' | AccessRole;
  folder_grants?: string[];
}

interface Invitation extends AccessFlags {
  id: string;
  email: string;
  role: AccessRole;
  sent_at: string;
  expires_at: string;
  retry_after_ms: number;
  folder_grants?: string[];
}

const memberFlags: AccessFlags = {
  can_manage_files: true,
  can_delete_files: false,
  can_manage_privacy: false,
  can_create_folders: true,
  can_delete_folders: false,
  can_manage_logs: true,
  can_manage_credentials: false,
  can_manage_members: false,
};

const rolePresets: Array<{
  role: AccessRole;
  title: string;
  copy: string;
  flags: AccessFlags;
}> = [
  {
    role: 'admin',
    title: 'Admin',
    copy: 'Operate the workspace, including credentials and people.',
    flags: {
      can_manage_files: true,
      can_delete_files: true,
      can_manage_privacy: true,
      can_create_folders: true,
      can_delete_folders: true,
      can_manage_logs: true,
      can_manage_credentials: true,
      can_manage_members: true,
    },
  },
  {
    role: 'member',
    title: 'Member',
    copy: 'Write files and create folders. Deletion and privacy stay off.',
    flags: memberFlags,
  },
  {
    role: 'viewer',
    title: 'Viewer',
    copy: 'Read files and logs. Cannot change anything.',
    flags: {
      can_manage_files: false,
      can_delete_files: false,
      can_manage_privacy: false,
      can_create_folders: false,
      can_delete_folders: false,
      can_manage_logs: false,
      can_manage_credentials: false,
      can_manage_members: false,
    },
  },
];

const accessItems: Array<{
  key: keyof AccessFlags;
  label: string;
  on: string;
  off: string;
  icon: typeof Folder;
  help: string;
}> = [
  {
    key: 'can_manage_files',
    label: 'Files',
    on: 'Write',
    off: 'Read',
    icon: Folder,
    help: 'Upload and replace objects. Does not include deleting them or changing public/private.',
  },
  {
    key: 'can_delete_files',
    label: 'Delete',
    on: 'Allowed',
    off: 'Blocked',
    icon: Trash2,
    help: 'Permanently remove objects. Keep this off unless they should empty storage.',
  },
  {
    key: 'can_manage_privacy',
    label: 'Privacy',
    on: 'Change',
    off: 'Locked',
    icon: Lock,
    help: 'Switch files between public CDN delivery and private links.',
  },
  {
    key: 'can_create_folders',
    label: 'New folder',
    on: 'Allowed',
    off: 'Blocked',
    icon: FolderPlus,
    help: 'Create folders. Existing folders stay unless Folders delete is on.',
  },
  {
    key: 'can_delete_folders',
    label: 'Folders',
    on: 'Delete',
    off: 'Keep',
    icon: Folder,
    help: 'Delete a folder and every object inside it.',
  },
  {
    key: 'can_manage_logs',
    label: 'Logs',
    on: 'Write',
    off: 'Read',
    icon: ScrollText,
    help: 'Ingest log events. Read stays available even when this is off.',
  },
  {
    key: 'can_manage_credentials',
    label: 'API / S3',
    on: 'Manage',
    off: 'Hidden',
    icon: KeyRound,
    help: 'Create and revoke REST API keys and S3 credentials.',
  },
  {
    key: 'can_manage_members',
    label: 'People',
    on: 'Manage',
    off: 'Hidden',
    icon: Users,
    help: 'Invite people, change roles, and remove members.',
  },
];

function flagsForRole(role: AccessRole): AccessFlags {
  return rolePresets.find((item) => item.role === role)?.flags ?? memberFlags;
}

function sameFlags(left: AccessFlags, right: AccessFlags) {
  return accessItems.every((item) => left[item.key] === right[item.key]);
}

function formatRemaining(ms: number) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (minutes <= 0) return `${seconds}s`;
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
}

function initials(name?: string, email?: string) {
  const source = name?.trim() || email?.trim() || '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase();
  return source.slice(0, 2).toUpperCase();
}

function parseFolders(value: string) {
  return [
    ...new Set(
      value
        .split(',')
        .map((item) => item.trim().replaceAll('\\', '/').replace(/^\/+|\/+$/g, ''))
        .filter(Boolean),
    ),
  ];
}

function AccessMatrix({
  value,
  onChange,
  disabled,
  compact,
}: {
  value: AccessFlags;
  onChange?: (next: AccessFlags) => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  return (
    <div className={compact ? 'people-matrix people-matrix--compact' : 'people-matrix'}>
      {accessItems.map((item) => {
        const on = value[item.key];
        return (
          <label key={item.key} className={on ? 'is-on' : undefined}>
            <item.icon />
            <span>
              <strong>{item.label}</strong>
              <small>{on ? item.on : item.off}</small>
            </span>
            <em className="people-tip">{item.help}</em>
            <button
              type="button"
              role="switch"
              aria-checked={on}
              aria-label={`${item.label}: ${item.help}`}
              disabled={disabled || !onChange}
              onClick={(event) => {
                event.currentTarget.blur();
                onChange?.({ ...value, [item.key]: !on });
              }}
            />
          </label>
        );
      })}
    </div>
  );
}

function FolderScope({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState(value.join(', '));
  useEffect(() => {
    setDraft(value.join(', '));
  }, [value.join('|')]);
  const commit = () => {
    const next = parseFolders(draft);
    if (next.join('|') === value.join('|')) return;
    onChange(next);
  };
  return (
    <label className="people-folders">
      <span>Folder access</span>
      <input
        value={draft}
        disabled={disabled}
        placeholder="All folders. Or media/ads, reports"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          }
        }}
      />
      <small>{value.length ? value.join(' · ') : 'Entire workspace'}</small>
    </label>
  );
}

export function WorkspacePeople() {
  const [section, setSection] = useState<'members' | 'invitations' | 'settings'>('members');
  const { user } = useAuth();
  const client = useQueryClient();
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string }>();
  const [inviteRole, setInviteRole] = useState<AccessRole>('member');
  const [inviteFlags, setInviteFlags] = useState<AccessFlags>(memberFlags);
  const [inviteFolders, setInviteFolders] = useState<string[]>([]);
  const [now, setNow] = useState(Date.now());
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceAvatar, setWorkspaceAvatar] = useState('');
  const [workspaceWait, setWorkspaceWait] = useState(0);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const photoInput = useRef<HTMLInputElement>(null);
  const isOwner = user?.workspace?.role === 'owner';
  const workspaceLocked = user?.plan_code === 'locked';
  const canManage = user?.workspace?.permissions.members !== false && !workspaceLocked;
  const customized = !sameFlags(inviteFlags, flagsForRole(inviteRole));
  const people = useQuery({
    queryKey: ['workspace-people'],
    queryFn: () => api<{ members: Member[]; invitations: Invitation[] }>('/workspace/members'),
  });
  const workspaces = useQuery({
    queryKey: ['workspaces'],
    queryFn: () =>
      api<{
        workspaces: Array<{
          owner_user_id: string;
          name: string;
          avatar_url?: string | null;
          avatar_retry_after_ms?: number;
          is_current?: boolean;
        }>;
      }>('/workspace'),
  });
  const currentWorkspace = workspaces.data?.workspaces.find((item) => item.is_current);
  const invite = useMutation({
    mutationFn: (body: { email: string; role: AccessRole; folder_grants: string[] } & AccessFlags) =>
      api('/workspace/invitations', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      setNotice({ tone: 'success', text: 'Invitation sent.' });
      void client.invalidateQueries({ queryKey: ['workspace-people'] });
    },
    onError: (error: Error) => setNotice({ tone: 'error', text: error.message }),
  });
  const resend = useMutation({
    mutationFn: (id: string) => api(`/workspace/invitations/${id}/resend`, { method: 'POST' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['workspace-people'] }),
    onError: (error: Error) => setNotice({ tone: 'error', text: error.message }),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api(`/workspace/invitations/${id}`, { method: 'DELETE' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['workspace-people'] }),
  });
  const updateInvite = useMutation({
    mutationFn: (input: { id: string; role: AccessRole; folder_grants?: string[] } & Partial<AccessFlags>) =>
      api(`/workspace/invitations/${input.id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['workspace-people'] }),
  });
  const updateMember = useMutation({
    mutationFn: (input: { id: string; role: AccessRole; folder_grants?: string[] } & Partial<AccessFlags>) =>
      api(`/workspace/members/${input.id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['workspace-people'] }),
  });
  const removeMember = useMutation({
    mutationFn: (id: string) => api(`/workspace/members/${id}`, { method: 'DELETE' }),
    onSuccess: () => void client.invalidateQueries({ queryKey: ['workspace-people'] }),
  });

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    if (!currentWorkspace) return;
    setWorkspaceName(currentWorkspace.name);
    setWorkspaceAvatar(currentWorkspace.avatar_url ?? '');
    setWorkspaceWait(Date.now() + (currentWorkspace.avatar_retry_after_ms ?? 0));
  }, [currentWorkspace?.name, currentWorkspace?.avatar_url, currentWorkspace?.avatar_retry_after_ms]);
  const photoRemaining = Math.max(0, workspaceWait - now);
  const formatWait = (ms: number) => {
    const total = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;
    return minutes > 0 ? `${minutes}m ${String(seconds).padStart(2, '0')}s` : `${seconds}s`;
  };
  const saveWorkspace = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const result = await api<{ workspace: { name: string; avatar_url?: string; avatar_retry_after_ms?: number } }>(
        '/workspace/profile',
        { method: 'PATCH', body: JSON.stringify({ name: workspaceName }) },
      );
      setWorkspaceName(result.workspace.name);
      setNotice({ tone: 'success', text: 'Workspace name updated.' });
      void client.invalidateQueries({ queryKey: ['workspaces'] });
    } catch (error: any) {
      setNotice({ tone: 'error', text: error.message });
    }
  };
  const saveWorkspacePhoto = async (crop: CropRect) => {
    if (!photoFile) return;
    setPhotoBusy(true);
    try {
      const body = new FormData();
      body.set('photo', photoFile);
      body.set('crop', JSON.stringify(crop));
      const result = await api<{ avatar: { url: string }; avatar_retry_after_ms: number }>('/workspace/avatar', {
        method: 'POST',
        body,
      });
      setPhotoFile(null);
      setWorkspaceAvatar(result.avatar.url);
      setWorkspaceWait(Date.now() + (result.avatar_retry_after_ms ?? 60 * 60 * 1000));
      setNotice({ tone: 'success', text: 'Workspace photo updated.' });
      void client.invalidateQueries({ queryKey: ['workspaces'] });
    } catch (error: any) {
      setNotice({ tone: 'error', text: error.message });
    } finally {
      setPhotoBusy(false);
    }
  };

  const sendInvite = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    invite.mutate({
      email: String(form.get('email') ?? ''),
      role: inviteRole,
      folder_grants: inviteFolders,
      ...inviteFlags,
    });
    event.currentTarget.reset();
    setInviteRole('member');
    setInviteFlags(memberFlags);
    setInviteFolders([]);
  };

  return (
    <div className="dashboard-page people-page">
      <div className="dash-heading">
        <div className="dash-heading__copy">
          <p>Control</p>
          <h1>Workspace</h1>
        </div>
      </div>
      {notice && <div className={`settings-notice settings-notice--${notice.tone}`}>{notice.text}</div>}

      <nav className="dashboard-section-nav" aria-label="Workspace sections">
        <button type="button" aria-current={section === 'members' ? 'page' : undefined} onClick={() => setSection('members')}>Members</button>
        <button type="button" aria-current={section === 'invitations' ? 'page' : undefined} onClick={() => setSection('invitations')}>Invitations</button>
        {isOwner && <button type="button" aria-current={section === 'settings' ? 'page' : undefined} onClick={() => setSection('settings')}>Workspace settings</button>}
      </nav>

      {isOwner ? (
        <form hidden={section !== 'settings'} className="people-composer workspace-identity" onSubmit={saveWorkspace}>
          <div className="people-composer__head">
            <h2>Workspace identity</h2>
            <p>This name and photo appear in the sidebar, invitations, and member views.</p>
          </div>
          <div className="profile-photo">
            <span className="profile-photo__preview">
              {workspaceAvatar ? <img src={workspaceAvatar} alt="" /> : workspaceName.slice(0, 1).toUpperCase()}
            </span>
            <div>
              <strong>Workspace photo</strong>
              <p>JPEG, PNG, WebP or GIF, up to 10 MB. Saved as a 512px square. Motion is kept as an animated WebP.</p>
              <input
                ref={photoInput}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
                hidden
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.currentTarget.value = '';
                  if (file) setPhotoFile(file);
                }}
              />
              <button
                className="button button--dash-quiet"
                type="button"
                disabled={photoRemaining > 0}
                onClick={() => photoInput.current?.click()}
              >
                {photoRemaining > 0 ? `Change photo in ${formatWait(photoRemaining)}` : 'Change photo'}
              </button>
            </div>
          </div>
          <label className="people-email">
            <span>Name</span>
            <input pattern=".*\S.*" title="Enter a workspace name with visible characters." value={workspaceName} minLength={2} maxLength={80} required onChange={(event) => setWorkspaceName(event.target.value)} />
          </label>
          <div className="people-composer__actions">
            <button className="button button--dash-primary">Save workspace</button>
          </div>
        </form>
      ) : null}

      <form hidden={section !== 'invitations'} className="people-composer" onSubmit={sendInvite}>
        <div className="people-composer__head">
          <h2>Add a member</h2>
          <p>
            {workspaceLocked
              ? 'Invites stay closed until this workspace has a paid plan.'
              : 'They keep their own account. This only adds them to the current workspace.'}
          </p>
        </div>
        <label className="people-email">
          <span>Email</span>
          <input maxLength={320} name="email" type="email" required placeholder="alex@company.com" disabled={!canManage} />
        </label>
        <fieldset className="people-presets">
          <legend>Preset</legend>
          <div role="radiogroup" aria-label="Role preset">
            {rolePresets.map((preset) => (
              <button
                key={preset.role}
                type="button"
                role="radio"
                aria-checked={inviteRole === preset.role}
                className={inviteRole === preset.role ? 'is-on' : undefined}
                onClick={() => {
                  setInviteRole(preset.role);
                  setInviteFlags(preset.flags);
                }}
              >
                <strong>{preset.title}</strong>
                <span>{preset.copy}</span>
              </button>
            ))}
          </div>
        </fieldset>
        <div className="people-access">
          <div>
            <span>Access</span>
            <small>{customized ? 'Customized from preset' : `${inviteRole} defaults`}</small>
          </div>
          <AccessMatrix value={inviteFlags} onChange={setInviteFlags} disabled={!canManage} />
        </div>
        <FolderScope value={inviteFolders} onChange={setInviteFolders} disabled={!canManage} />
        <div className="people-composer__actions">
          <button
            className="button button--dash-primary"
            disabled={invite.isPending || !canManage}
            data-state={invite.isPending ? 'loading' : undefined}
          >
            <UserPlus />
            {invite.isPending ? 'Sending...' : 'Send invitation'}
          </button>
        </div>
      </form>

      <section className="people-block" hidden={section !== 'invitations'}>
        <div className="people-block__head">
          <h2>Pending invitations</h2>
          <p>A new copy can be sent after 10 minutes.</p>
        </div>
        <div className="people-roster">
          {people.data?.invitations.length ? (
            people.data.invitations.map((item) => {
              const remaining = Math.max(0, 10 * 60 * 1000 - (now - Date.parse(item.sent_at)));
              return (
                <article key={item.id} className="people-card">
                  <header className="people-card__top">
                    <span className="people-avatar" aria-hidden="true">
                      {initials(item.email, item.email)}
                    </span>
                    <div className="people-identity">
                      <strong>{item.email}</strong>
                      <p>
                        Sent{' '}
                        {new Intl.DateTimeFormat('en', { dateStyle: 'medium', timeStyle: 'short' }).format(
                          new Date(item.sent_at),
                        )}
                      </p>
                    </div>
                    <div className="people-actions">
                      <select
                        className="people-role"
                        value={item.role}
                        disabled={!canManage}
                        aria-label="Invitation role"
                        onChange={(event) =>
                          updateInvite.mutate({ id: item.id, role: event.target.value as AccessRole })
                        }
                      >
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                        <option value="viewer">Viewer</option>
                      </select>
                      {remaining > 0 ? (
                        <small>Send again in {formatRemaining(remaining)}</small>
                      ) : (
                        <button className="button button--dash-quiet" onClick={() => resend.mutate(item.id)}>
                          <Mail />
                          Send again
                        </button>
                      )}
                      <button className="icon-button" aria-label="Revoke invitation" onClick={() => revoke.mutate(item.id)}>
                        <Trash2 />
                      </button>
                    </div>
                  </header>
                  <div className="people-card__body">
                    <FolderScope
                      value={item.folder_grants ?? []}
                      disabled={!canManage}
                      onChange={(folders) =>
                        updateInvite.mutate({ id: item.id, role: item.role, folder_grants: folders })
                      }
                    />
                    <AccessMatrix
                      compact
                      value={item}
                      disabled={!canManage}
                      onChange={(flags) => updateInvite.mutate({ id: item.id, role: item.role, ...flags })}
                    />
                  </div>
                </article>
              );
            })
          ) : (
            <div className="people-empty">
              <Mail />
              <p>No invitations waiting.</p>
            </div>
          )}
        </div>
      </section>

      <section className="people-block" hidden={section !== 'members'}>
        <div className="people-block__head">
          <h2>Members</h2>
          <p>Viewers read. Members write files. Delete, privacy, and folder removal stay explicit.</p>
        </div>
        <div className="people-roster">
          {people.data?.members.map((member) => (
            <article
              key={member.member_user_id}
              className={member.role === 'owner' ? 'people-card people-card--owner' : 'people-card'}
            >
              <header className="people-card__top">
                <span className="people-avatar" aria-hidden="true">
                  {member.role === 'owner' ? <ShieldCheck /> : initials(member.display_name, member.email)}
                </span>
                <div className="people-identity">
                  <strong>{member.display_name}</strong>
                  <p>{member.email}</p>
                  {member.role === 'owner' ? (
                    <small className="people-owner-copy">Owns storage, logs, credentials, and billing.</small>
                  ) : null}
                </div>
                {member.role === 'owner' ? (
                  <span className="people-owner">Owner</span>
                ) : (
                  <div className="people-actions">
                    <select
                      className="people-role"
                      value={member.role}
                      disabled={!canManage}
                      aria-label="Member role"
                      onChange={(event) =>
                        updateMember.mutate({
                          id: member.member_user_id,
                          role: event.target.value as AccessRole,
                        })
                      }
                    >
                      <option value="admin">Admin</option>
                      <option value="member">Member</option>
                      <option value="viewer">Viewer</option>
                    </select>
                    <button
                      className="icon-button"
                      aria-label="Remove member"
                      onClick={() => removeMember.mutate(member.member_user_id)}
                    >
                      <Trash2 />
                    </button>
                  </div>
                )}
              </header>
              {member.role !== 'owner' ? (
                <div className="people-card__body">
                  <FolderScope
                    value={member.folder_grants ?? []}
                    disabled={!canManage}
                    onChange={(folders) =>
                      updateMember.mutate({
                        id: member.member_user_id,
                        role: member.role as AccessRole,
                        folder_grants: folders,
                      })
                    }
                  />
                  <AccessMatrix
                    compact
                    value={member}
                    disabled={!canManage}
                    onChange={(flags) =>
                      updateMember.mutate({
                        id: member.member_user_id,
                        role: member.role as AccessRole,
                        ...flags,
                      })
                    }
                  />
                </div>
              ) : null}
            </article>
          ))}
        </div>
      </section>
      {photoFile ? (
        <AvatarCropper
          file={photoFile}
          busy={photoBusy}
          onCancel={() => setPhotoFile(null)}
          onConfirm={(crop) => void saveWorkspacePhoto(crop)}
        />
      ) : null}
    </div>
  );
}
